import { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Users, Search, RefreshCw, CheckCircle2, Ban, Eye, EyeOff,
    Loader2, Package, Clock, Link2, Zap, LogIn,
} from 'lucide-react';
import { toast } from 'sonner';

import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { portalCustomersAdminAPI } from '../lib/api';
import { PORTAL_LINK } from '../config/appMode';

const TABS = [
    { key: 'pending',  label: 'Pending review' },
    { key: 'approved', label: 'Approved' },
    { key: 'all',      label: 'All' },
];

const StatusPill = ({ approved }) => approved ? (
    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-medium">
        <CheckCircle2 size={11} /> Approved
    </span>
) : (
    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-medium">
        <Clock size={11} /> Pending
    </span>
);

const PortalCustomers = () => {
    const [tab, setTab] = useState('pending');
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [data, setData] = useState({ items: [], status_counts: {} });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState({}); // per-row busy flag

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (tab !== 'all') params.status = tab;
            if (debounced) params.q = debounced;
            const { data: d } = await portalCustomersAdminAPI.list(params);
            setData(d);
        } catch {
            toast.error('Could not load portal customers');
        } finally {
            setLoading(false);
        }
    }, [tab, debounced]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const counts = useMemo(() => data.status_counts || {}, [data]);

    const setRowBusy = (email, val) => setBusy((b) => ({ ...b, [email]: val }));

    const onApprove = async (email) => {
        setRowBusy(email, true);
        try {
            await portalCustomersAdminAPI.approve(email);
            toast.success(`Approved · welcome email sent to ${email}`);
            await fetchList();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Approve failed');
        } finally {
            setRowBusy(email, false);
        }
    };

    const onQuickLink = async (email) => {
        // Single-click admin shortcut: creates a CRM customer from the
        // portal snapshot AND approves + links the portal account in
        // one round-trip. Idempotent — clicking on an already-linked
        // account just runs the standard approve flow on the backend.
        if (!window.confirm(`Quick-create a CRM customer for ${email} and approve their portal account?`)) return;
        setRowBusy(email, true);
        try {
            const { data: rec } = await portalCustomersAdminAPI.quickLink(email);
            toast.success(`Linked · ${rec.crm_customer_code || 'CRM customer created'}`);
            await fetchList();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Quick-link failed');
        } finally {
            setRowBusy(email, false);
        }
    };

    const onViewAsCustomer = async (email) => {
        // Mint a portal token via the admin impersonate backdoor, then
        // open the portal in a new tab pre-authenticated as the customer.
        // We use sessionStorage (not localStorage) so the impersonation
        // dies when the admin closes the tab — keeps the audit trail
        // clean and prevents the token from leaking into the admin's
        // own browser session.
        setRowBusy(email, true);
        try {
            const { data } = await portalCustomersAdminAPI.impersonate(email);
            const handoff = window.btoa(JSON.stringify({ token: data.token, email: data.email, ts: Date.now() }));
            // Admin + portal share one host now — impersonation just
            // opens `/portal/impersonate?h=…` on the current origin.
            const url = `${window.location.origin}${PORTAL_LINK}/impersonate?h=${encodeURIComponent(handoff)}`;
            window.open(url, '_blank', 'noopener');
            toast.success(`Opened portal as ${email}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'View-as failed');
        } finally {
            setRowBusy(email, false);
        }
    };

    const onSuspend = async (email) => {
        if (!window.confirm(`Suspend ${email}? They will no longer be able to submit orders.`)) return;
        setRowBusy(email, true);
        try {
            await portalCustomersAdminAPI.suspend(email);
            toast.success('Account suspended');
            await fetchList();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Suspend failed');
        } finally {
            setRowBusy(email, false);
        }
    };

    const onTogglePricing = async (email, current) => {
        setRowBusy(email, true);
        try {
            await portalCustomersAdminAPI.setPricing(email, !current);
            toast.success(current ? 'Prices hidden for this customer' : 'Prices visible to this customer');
            await fetchList();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Update failed');
        } finally {
            setRowBusy(email, false);
        }
    };

    return (
        <div className="space-y-6" data-testid="portal-customers-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <p className="text-xs uppercase tracking-[3px] text-[#C08478] font-semibold mb-1">B2B Portal</p>
                    <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
                        <Users size={22} /> Portal Customers
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Approve B2B accounts and control which customers can see prices.</p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchList}
                    disabled={loading}
                    data-testid="portal-customers-refresh-btn"
                >
                    <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex bg-white border border-[#EFE5E1] rounded-full p-1" data-testid="portal-customers-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`portal-customers-tab-${t.key}`}
                            className={`px-4 py-1.5 text-sm rounded-full transition-all ${
                                tab === t.key ? 'bg-[#1A1A1A] text-white' : 'text-gray-600 hover:text-[#1A1A1A]'
                            }`}
                        >
                            {t.label}
                            <span className={`ml-2 text-[10px] ${tab === t.key ? 'opacity-80' : 'opacity-60'}`}>
                                {counts[t.key] ?? 0}
                            </span>
                        </button>
                    ))}
                </div>
                <div className="relative w-full sm:w-72">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Email, company, contact…"
                        className="pl-9 bg-white border-[#EFE5E1]"
                        data-testid="portal-customers-search"
                    />
                </div>
            </div>

            <div className="bg-white border border-[#EFE5E1] rounded-lg overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-gray-400" data-testid="portal-customers-loading">
                        <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Loading customers…
                    </div>
                ) : (data.items || []).length === 0 ? (
                    <div className="p-12 text-center" data-testid="portal-customers-empty">
                        <Package size={28} className="mx-auto text-gray-300 mb-3" />
                        <p className="text-sm text-gray-500">No customers match the current filter.</p>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-[#FBF8F6] text-[11px] uppercase tracking-wider text-gray-500">
                            <tr>
                                <th className="px-4 py-3 text-left">Customer</th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="px-4 py-3 text-left">Prices</th>
                                <th className="px-4 py-3 text-left">Signed up</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody data-testid="portal-customers-rows">
                            {data.items.map((c) => {
                                const rowBusy = busy[c.email];
                                return (
                                    <tr key={c.email} className="border-t border-[#F5EEEA]" data-testid={`portal-customer-row-${c.email}`}>
                                        <td className="px-4 py-3">
                                            <p className="font-medium text-[#1A1A1A]">{c.company_name || '—'}</p>
                                            <p className="text-[11px] text-gray-500">{c.contact_name} · {c.email}</p>
                                            {!c.profile_complete && (
                                                <p className="text-[10px] text-amber-700 mt-0.5">Profile incomplete</p>
                                            )}
                                            {c.crm_customer_code && (
                                                <p className="text-[10px] text-emerald-700 mt-0.5 inline-flex items-center gap-1" data-testid={`crm-link-${c.email}`}>
                                                    <Link2 size={10} /> CRM · {c.crm_customer_code}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3"><StatusPill approved={c.is_approved} /></td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <Switch
                                                    checked={c.can_see_prices}
                                                    onCheckedChange={() => onTogglePricing(c.email, c.can_see_prices)}
                                                    disabled={rowBusy}
                                                    data-testid={`pricing-toggle-${c.email}`}
                                                />
                                                <span className="text-[11px] text-gray-500 inline-flex items-center gap-1">
                                                    {c.can_see_prices ? <Eye size={11} /> : <EyeOff size={11} />}
                                                    {c.can_see_prices ? 'Visible' : 'Hidden'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-[11px] text-gray-500">
                                            {c.created_at?.slice(0, 10) || '—'}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="inline-flex items-center gap-1.5 justify-end">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => onViewAsCustomer(c.email)}
                                                    disabled={rowBusy}
                                                    title="Open the portal pre-authenticated as this customer (admin support shortcut)"
                                                    className="text-gray-500 hover:text-[#1A1A1A] hover:bg-[#FBF8F6]"
                                                    data-testid={`view-as-btn-${c.email}`}
                                                >
                                                    <LogIn size={12} className="mr-1" />
                                                    View as
                                                </Button>
                                                {c.is_approved ? (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => onSuspend(c.email)}
                                                        disabled={rowBusy}
                                                        className="border-red-200 text-red-600 hover:bg-red-50"
                                                        data-testid={`suspend-btn-${c.email}`}
                                                    >
                                                        {rowBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <Ban size={12} className="mr-1" />}
                                                        Suspend
                                                    </Button>
                                                ) : (
                                                    <>
                                                        {!c.crm_customer_code && c.profile_complete && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => onQuickLink(c.email)}
                                                                disabled={rowBusy}
                                                                title="Create a CRM customer from this portal profile and approve in one click"
                                                                className="border-[#C08478] text-[#C08478] hover:bg-[#FBF8F6]"
                                                                data-testid={`quick-link-btn-${c.email}`}
                                                            >
                                                                {rowBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <Zap size={12} className="mr-1" />}
                                                                Quick-link
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="sm"
                                                            onClick={() => onApprove(c.email)}
                                                            disabled={rowBusy}
                                                            className="bg-[#1A1A1A] hover:bg-[#333] text-white"
                                                            data-testid={`approve-btn-${c.email}`}
                                                        >
                                                            {rowBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <CheckCircle2 size={12} className="mr-1" />}
                                                            Approve
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default PortalCustomers;
